import { diff_match_patch } from 'diff-match-patch';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { appState } from './appState';
import { dom } from './dom';
import { stopDemoLoop } from './demo';
import { ensureProjectEntityContext, filterEntityMapsByText, saveProjectEntityMap, serializeEntityMaps } from './entityMaps';
import { updateFilesDisplay } from './files';
import { escapeHtml } from './htmlEscape';
import { t } from './i18n';
import { setAppState } from './ui';
import { AppState } from './types';
export async function handleMainAction() {
    if (appState.currentAppState === AppState.WRITE) {
        // Stop demo loop when user starts typing/sending a message
        stopDemoLoop();
        await performAnonymization();
    }
}
export async function performAnonymization() {
    const text = dom.inputText.value;
    const hasFiles = appState.uploadedFiles.length > 0;
    if (!text.trim() && !hasFiles) {
        return;
    }
    dom.mainActionBtn.disabled = true;
    dom.sendIcon.classList.add('d-none');
    const processingIcon = document.createElement('i');
    processingIcon.className = 'bi bi-shield-lock-fill fs-4 icon-processing';
    dom.mainActionBtn.appendChild(processingIcon);
    dom.mainActionBtn.classList.add('btn-processing');
    await new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 50)));
    try {
        let combinedText = text.trim();
        if (hasFiles) {
            const filesText = appState.uploadedFiles
                .map(file => `\n\n--- File: ${file.name} ---\n${file.content}`)
                .join('\n');
            combinedText = combinedText ? `${combinedText}${filesText}` : filesText.substring(2);
        }
        await ensureProjectEntityContext(appState.currentProjectId);
        const [result] = await Promise.all([
            anonymizeText(combinedText),
            new Promise(resolve => setTimeout(resolve, 1500))
        ]);
        const fullSnapshot = serializeEntityMaps(result.entityMaps);
        const messageSnapshot = filterEntityMapsByText(fullSnapshot, result.anonymizedText);
        displayResult(combinedText, result.anonymizedText, messageSnapshot);
        appState.currentEntityMaps = messageSnapshot;
        if (appState.currentProjectId) {
            await saveProjectEntityMap(appState.currentProjectId, fullSnapshot);
        }
        dom.currentAnonymizedText.value = result.anonymizedText;
        setAppState(AppState.REVIEW);
        if (appState.autoReviewEnabled) {
            requestAnimationFrame(() => {
                const confirmSendBtn = document.getElementById('confirmSendBtn');
                confirmSendBtn?.click();
            });
        }
    }
    catch (error) {
        const errorMessage = error instanceof Error ? error.message : t('errors.unknown');
        alert(t('alerts.anonymizationFailed', { error: errorMessage }));
    }
    finally {
        if (dom.mainActionBtn.contains(processingIcon)) {
            dom.mainActionBtn.removeChild(processingIcon);
        }
        dom.mainActionBtn.classList.remove('btn-processing');
        dom.sendIcon.classList.remove('d-none');
        if (appState.currentAppState === AppState.WRITE) {
            dom.mainActionBtn.disabled = false;
        }
    }
}
export async function anonymizeText(text) {
    if (!appState.sanitizationPipeline || !appState.entityAnonymizer || !appState.nerPipeline || !appState.entityContext) {
        throw new Error(t('errors.pipelineNotInitialized'));
    }
    const sanitizedText = appState.sanitizationPipeline.sanitize(text);
    const results = await appState.nerPipeline(sanitizedText);
    const detectedEntities = appState.entityAnonymizer.reconstructEntities(results.filter((token) => (token.entity_group || token.entity) !== 'O'));
    const anonymizedText = appState.entityAnonymizer.anonymize(sanitizedText, detectedEntities);
    return { anonymizedText, entityMaps: appState.entityContext.getAllMaps() };
}
export function handleClear() {
    dom.inputText.value = '';
    dom.currentAnonymizedText.value = '';
    dom.diffContent.innerHTML = '';
    dom.anonymizedContent.innerHTML = '';
    dom.entityMapsContent.innerHTML = '';
    dom.entityBadge.textContent = '0';
    appState.uploadedFiles.length = 0;
    appState.currentEntityMaps = null;
    updateFilesDisplay();
    setAppState(AppState.WRITE);
}
export function handleCopy() {
    navigator.clipboard.writeText(dom.anonymizedContent.innerText).then(() => {
        const originalText = dom.copyBtn.innerText;
        dom.copyBtn.innerText = t('alerts.copied');
        setTimeout(() => {
            dom.copyBtn.innerText = originalText;
        }, 2000);
    });
}
function displayResult(original, anonymized, entityMaps) {
    const dmp = new diff_match_patch();
    const diffs = dmp.diff_main(original, anonymized);
    dmp.diff_cleanupSemantic(diffs);
    dom.diffContent.innerHTML = dmp.diff_prettyHtml(diffs);
    dom.anonymizedContent.innerHTML = DOMPurify.sanitize(marked.parse(anonymized));
    displayEntityMaps(entityMaps);
}
function displayEntityMaps(entityMaps) {
    let total = 0;
    dom.entityMapsContent.innerHTML = '';
    const accordion = document.createElement('div');
    accordion.className = 'accordion accordion-flush';
    let index = 0;
    Object.entries(entityMaps).forEach(([type, entries]) => {
        const rows = Object.entries(entries);
        if (rows.length === 0) {
            return;
        }
        total += rows.length;
        const id = `ent-${index++}`;
        const item = document.createElement('div');
        item.className = 'accordion-item bg-transparent';
        item.innerHTML = `
      <h2 class="accordion-header">
        <button class="accordion-button collapsed bg-transparent small fw-bold" type="button" data-bs-toggle="collapse" data-bs-target="#${id}">
          ${escapeHtml(type)} <span class="badge bg-secondary ms-2">${rows.length}</span>
        </button>
      </h2>
      <div id="${id}" class="accordion-collapse collapse">
        <div class="accordion-body p-0">
          <table class="table table-sm table-hover mb-0 small">
            <tbody>
              ${rows.map(([orig, tag]) => `
                <tr><td><code>${escapeHtml(orig)}</code></td><td><span class="entity-tag">${escapeHtml(tag)}</span></td></tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
        accordion.appendChild(item);
    });
    dom.entityBadge.textContent = total.toString();
    dom.entityMapsContent.appendChild(accordion);
}
